import { Moon, Sun } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { useTheme } from "./ThemeProvider";
import { cn } from "@/lib/utils";

interface Props {
  className?: string;
}

export function ThemeToggle({ className }: Props) {
  const { theme, toggle } = useTheme();
  const reduce = useReducedMotion();
  const isDark = theme === "dark";

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      data-testid="button-theme-toggle"
      className={cn(
        "relative inline-flex h-8 w-14 items-center rounded-full",
        "border border-[color:var(--glass-stroke)]",
        "bg-[color:var(--glass-fill-soft)] backdrop-blur-md",
        "transition-colors hover:bg-[color:var(--glass-fill)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
    >
      <span className="sr-only">Toggle theme</span>
      <motion.span
        layout={!reduce}
        transition={
          reduce
            ? { duration: 0 }
            : { type: "spring", stiffness: 380, damping: 28 }
        }
        className={cn(
          "absolute top-1 inline-flex h-6 w-6 items-center justify-center",
          "rounded-full shadow-sm",
          "bg-[hsl(var(--card))] text-[hsl(var(--foreground))]",
          "border border-[color:var(--glass-stroke)]",
          isDark ? "left-[calc(100%-1.75rem)]" : "left-1",
        )}
      >
        {isDark ? <Moon className="h-3.5 w-3.5" /> : <Sun className="h-3.5 w-3.5" />}
      </motion.span>
    </button>
  );
}
